// src/stores/uiStore.ts
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { UIState, GameInstruction } from '@/types/ui';
import { useCompletedDemosStore } from '@/stores/completedDemosStore';
import { useGamePlayStore } from '@/stores/gamePlayStore';

// Define the valid animation types.
export type AnimationType = 'stars' | 'fireworks' | 'explosions' | 'side';

// Define an interface for the animation component.
export interface AnimationComponent {
  runAnimation: (
    type: AnimationType,
    duration?: number,
    intensity?: number
  ) => void;
}

export const useUIStore = defineStore('ui', () => {
  const completedDemosStore = useCompletedDemosStore();
  const gamePlayStore = useGamePlayStore();

  const state = ref<UIState>({
    currentStep: 'introduction',
    loading: false,
    instructions: [],
    mainInstructions: [],
    isAnimationRunning: false,
    isUpdatingApp: false,
    updateMessage: '',
  });

  // Separate flag for visual effects that shouldn't block interaction
  const isVisualEffectRunning = ref(false);

  // Prevent transition loops
  const preventTransition = ref(false);

  // New ref to hold a reference to the animation component.
  const animationComponent = ref<AnimationComponent | null>(null);

  // Existing actions
  function setCurrentStep(step: string): void {
    state.value.currentStep = step.toLowerCase();
  }

  /**
   * Determines if a demo should be shown instead of play based on game settings
   * This centralizes the transition logic that was previously in BaseGame
   * @param gameSettings Game settings from the gameStore
   * @param demoState State from the demoStore
   * @returns Whether to show demo ('demo') or play ('play')
   */
  function shouldShowDemoOrPlay({
    gameSettings,
    demoState,
    gameLevel,
  }: {
    gameSettings: Record<string, unknown> | null;
    demoState: {
      returnFromDemo: boolean;
      demoCompleted: boolean;
      isDemoMidGame: boolean;
      processingPlayTransition: boolean;
    };
    gameLevel: number;
  }): string {
    const coerceBoolean = (value: unknown): boolean => {
      if (typeof value === 'boolean') return value;
      if (typeof value === 'string') return value.toLowerCase() === 'true';
      if (typeof value === 'number') return value === 1;
      return false;
    };
    console.log('Checking if demo should be shown with:', {
      gameSettings,
      demoState,
      gameLevel,
    });

    // If we're in the middle of a transition, prevent changing
    if (preventTransition.value || demoState.processingPlayTransition) {
      console.log('Transition prevented - already in progress');
      return 'play'; // Default to play if we're in a transition
    }

    const backendRequestsDemo = coerceBoolean(gameSettings?.show_demo);
    const gameId = Number(gamePlayStore.gameId);
    const demoAlreadyCompletedForLevel =
      Number.isFinite(gameId) &&
      gameId > 0 &&
      Number.isFinite(gameLevel) &&
      gameLevel > 1 &&
      completedDemosStore.isDemoCompleted(gameId, gameLevel);

    // Check if we should show demo based on settings and state
    const shouldShowDemo =
      backendRequestsDemo &&
      !demoState.returnFromDemo &&
      !demoState.demoCompleted &&
      !demoAlreadyCompletedForLevel;

    console.log('Demo criteria check:', {
      backendRequestsDemo,
      returnFromDemo: demoState.returnFromDemo,
      demoCompleted: demoState.demoCompleted,
      demoAlreadyCompletedForLevel,
      gameLevel,
      shouldShowDemo,
    });

    if (shouldShowDemo) {
      console.log('Should show demo based on criteria');

      // Mark transition in progress to prevent loops
      preventTransition.value = true;

      // Reset prevention flag after a delay
      setTimeout(() => {
        preventTransition.value = false;
      }, 500);

      return 'demo';
    }

    return 'play';
  }

  function setLoading(status: boolean): void {
    state.value.loading = status;
  }

  function setInstructions(newInstructions: GameInstruction[]): void {
    state.value.instructions = newInstructions;
  }

  function setMainInstructions(newInstructions: GameInstruction[]): void {
    state.value.mainInstructions = newInstructions;
  }

  function startAppUpdate(message?: string): void {
    state.value.isUpdatingApp = true;
    state.value.updateMessage =
      message ?? 'We’re updating your games. This will only take a moment.';
  }

  function finishAppUpdate(): void {
    state.value.isUpdatingApp = false;
    state.value.updateMessage = '';
  }

  // New action to register the animation component.
  function registerAnimationComponent(component: AnimationComponent): void {
    animationComponent.value = component;
  }

  // New action to trigger a visual animation that doesn't block interaction
  function triggerAnimation(
    type: AnimationType,
    duration: number = 5,
    intensity: number = 5,
    blockInteraction: boolean = false
  ): void {
    if (
      animationComponent.value &&
      typeof animationComponent.value.runAnimation === 'function'
    ) {
      // Only set the blocking flag if explicitly requested
      if (blockInteraction) {
        state.value.isAnimationRunning = true;
      }

      // Always set the visual effect flag
      isVisualEffectRunning.value = true;

      animationComponent.value.runAnimation(type, duration, intensity);

      setTimeout(() => {
        if (blockInteraction) {
          state.value.isAnimationRunning = false;
        }
        isVisualEffectRunning.value = false;
      }, duration * 1000);
    } else {
      console.warn('Animation component is not registered.');
    }
  }

  function triggerRandomAnimation(blockInteraction: boolean = false) {
    const animations = [
      { name: 'stars' },
      { name: 'fireworks' },
      { name: 'explosions' },
      { name: 'side' },
    ];

    const animation = animations[Math.floor(Math.random() * animations.length)];
    console.log(`Running animation: ${animation.name}`);
    triggerAnimation(animation.name, 2, 5, blockInteraction);
  }

  return {
    // State
    state,

    // Getters
    currentStep: computed(() => state.value.currentStep.toLowerCase()),
    loading: computed(() => state.value.loading),
    instructions: computed(() => state.value.instructions),
    mainInstructions: computed(() => state.value.mainInstructions),
    isAnimationRunning: computed(() => state.value.isAnimationRunning),
    isVisualEffectRunning: computed(() => isVisualEffectRunning.value),
    isUpdatingApp: computed(() => state.value.isUpdatingApp),
    updateMessage: computed(() => state.value.updateMessage),
    preventTransition,

    // Existing Actions
    setCurrentStep,
    setLoading,
    setInstructions,
    setMainInstructions,
    startAppUpdate,
    finishAppUpdate,

    // Demo management
    shouldShowDemoOrPlay,

    // New Actions for Animations
    registerAnimationComponent,
    triggerAnimation,
    triggerRandomAnimation,
  };
});
